<template>
  <transition name="fade">
    <div
      v-if="internalVisible"
      class="fu-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Update in progress"
    >
      <div class="fu-container" :class="{ 'fu-failed': status === 'failed' }">
        <button
          v-if="closable"
          class="fu-close"
          @click="handleClose"
          aria-label="Close updater"
        >
          ✕
        </button>

        <div class="fu-header">
          <h3 class="fu-title">
            {{ status === "failed" ? "下载失败" : "正在更新" }}
          </h3>
          <p class="fu-sub" v-if="titleSub">{{ titleSub }}</p>
        </div>

        <div class="fu-body">
          <!-- ✅ 正常更新状态 -->
          <template v-if="status === 'updating'">
            <div class="fu-progress-wrap">
              <div class="fu-progress-bar">
                <div
                  class="fu-progress-fill"
                  :style="{ width: progressPercent + '%' }"
                ></div>
              </div>
              <div class="fu-percent" v-if="showPercent">
                {{ progressPercent }}%
              </div>
            </div>

            <div class="fu-content">
              <slot name="content">
                <template v-if="Array.isArray(renderContent)">
                  <div
                    v-for="(line, idx) in renderContent"
                    :key="idx"
                    class="fu-line"
                  >
                    {{ line }}
                  </div>
                </template>
                <template v-else>
                  <div class="fu-line">{{ renderContent }}</div>
                </template>
              </slot>
            </div>
          </template>

          <!-- ✅ 下载失败状态 -->
          <template v-else-if="status === 'failed'">
            <div class="fu-fail-box">
              <div class="fu-fail-icon">⚠️</div>
              <div class="fu-fail-text">下载失败，请尝试手动下载：</div>
              <div class="fu-fail-url">{{ failedUrl }}</div>
              <div class="fu-fail-actions">
                <button class="fu-btn fu-btn-copy" @click="handleCopy">
                  复制地址
                </button>
              </div>
            </div>
          </template>
          <div class="fu-actions" v-if="closable">
            <button class="fu-btn" @click="handleCancel">关闭</button>
          </div>
        </div>

        <div class="fu-footer" v-if="status === 'updating'">
          <b>请勿关闭应用，更新正在进行中……</b>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "FullscreenUpdate",
  props: {
    visible: { type: Boolean, default: false },
    progress: { type: Number, default: 0 },
    content: { type: [String, Array], default: "" },
    closable: { type: Boolean, default: true },
    showPercent: { type: Boolean, default: true },
    titleSub: { type: String, default: "" },
    autoCloseOnFinish: { type: Boolean, default: true },
  },
  data() {
    return {
      internalVisible: this.visible,
      currentProgress: this.normalizeProgress(this.progress),
      paused: false,
      renderContent: this.content,
      status: "updating", // ✅ updating | failed
      failedUrl: "", // ✅ 失败下载地址
    };
  },
  watch: {
    visible(v) {
      this.internalVisible = v;
    },
    internalVisible(v) {
      this.$emit("update:visible", v);
    },
    progress(v) {
      this.setProgress(v);
    },
    content(v) {
      this.setContent(v);
    },
  },
  computed: {
    progressPercent() {
      return this.normalizeProgress(this.currentProgress);
    },
  },
  methods: {
    normalizeProgress(n) {
      let num = Number(n) || 0;
      if (num < 0) num = 0;
      if (num > 100) num = 100;
      return Math.round(num);
    },
    start() {
      this.status = "updating";
      this.internalVisible = true;
      this.paused = false;
      if (typeof this.currentProgress !== "number") this.currentProgress = 0;
      this.$emit("start");
    },
    close() {
      this.internalVisible = false;
      this.$emit("close");
    },
    handleClose() {
      if (!this.closable) return;
      this.close();
    },
    setProgress(n) {
      if (this.status === "failed") return;
      const p = this.normalizeProgress(n);
      this.currentProgress = p;
      this.$emit("progress", p);
      if (p >= 100) {
        this.$emit("finish");
        if (this.autoCloseOnFinish) {
          setTimeout(() => {
            this.close();
          }, 700);
        }
      }
    },
    setContent(data, replace = true) {
      if (replace) {
        this.renderContent = data;
      } else {
        if (!Array.isArray(this.renderContent))
          this.renderContent = [this.renderContent];
        if (Array.isArray(data))
          this.renderContent = this.renderContent.concat(data);
        else this.renderContent.push(data);
      }
      this.$emit("content", this.renderContent);
    },
    pauseOrResume() {
      this.paused = !this.paused;
      this.$emit(this.paused ? "pause" : "resume");
    },
    handleCancel() {
      this.$emit("cancel");
      this.close();
    },
    // ✅ 设置失败状态
    setFailed(url) {
      this.status = "failed";
      this.failedUrl = url || "";
      this.currentProgress = 0;
      this.renderContent = "下载失败，请尝试手动下载。";
      this.$emit("failed", url);
    },
    // ✅ 复制下载地址
    handleCopy() {
      this.copyText(this.failedUrl)
        .then(() => {
          this.$emit("copied", this.failedUrl);
          alert("下载地址已复制到剪贴板");
        })
        .catch(() => {
          alert("复制失败，请手动复制");
        });
    },
    copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        // ✅ 优先使用现代 API
        return navigator.clipboard.writeText(text);
      } else {
        // 🚧 兼容旧浏览器
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed"; // 防止滚动影响
        textarea.style.opacity = "0"; // 隐藏
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          const result = document.execCommand("copy");
          document.body.removeChild(textarea);
          return result
            ? Promise.resolve()
            : Promise.reject(new Error("execCommand failed"));
        } catch (err) {
          document.body.removeChild(textarea);
          return Promise.reject(err);
        }
      }
    },
  },
  mounted() {
    this.currentProgress = this.normalizeProgress(this.progress);
    this.renderContent = this.content;
  },
};
</script>

<style scoped>
.fu-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(8, 12, 20, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.fu-container {
  width: 90%;
  max-width: 860px;
  background: #333;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 8px 30px rgba(2, 6, 23, 0.6);
  color: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
}

.fu-container > * + * {
  margin-top: 18px;
}

.fu-close {
  position: absolute;
  right: 12px;
  top: 12px;
  border: none;
  background: transparent;
  color: #ddd;
  font-size: 18px;
  cursor: pointer;
}

.fu-header {
  text-align: left;
}

.fu-title {
  margin: 0;
  font-size: 22px;
}

.fu-sub {
  margin: 4px 0 0;
  color: #bfc9d6;
  font-size: 13px;
}

.fu-body {
  display: flex;
  flex-direction: column;
}

.fu-body > * + * {
  margin-top: 14px;
}

.fu-progress-wrap {
  display: flex;
  align-items: center;
}

.fu-progress-bar {
  flex: 1;
  height: 14px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.fu-progress-fill {
  height: 100%;
  width: 0%;
  background: linear-gradient(
    90deg,
    rgba(0, 210, 202, 1),
    rgba(0, 150, 255, 1)
  );
  transition: width 300ms ease;
}

.fu-percent {
  min-width: 48px;
  text-align: right;
  font-weight: 600;
}

.fu-content {
  max-height: 220px;
  overflow: auto;
  padding-right: 6px;
}

.fu-line {
  padding: 6px 0;
  font-size: 14px;
  color: #e6eef6;
}

.fu-actions {
  display: flex;
}

.fu-actions > * + * {
  margin-left: 10px;
}

.fu-btn {
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: #fff;
  cursor: pointer;
}

.fu-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

.fu-footer {
  text-align: right;
  color: #9fb0c5;
  font-size: 16px;
}

/* === 下载失败状态 === */
.fu-failed {
  background: rgba(50, 20, 20, 0.9);
}

.fu-fail-box {
  background: rgba(255, 0, 0, 0.08);
  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  color: #ff6b6b;
}

.fu-fail-icon {
  font-size: 38px;
  margin-bottom: 10px;
}

.fu-fail-text {
  font-size: 16px;
  margin-bottom: 8px;
  color: #ff8a8a;
}

.fu-fail-url {
  font-size: 14px;
  word-break: break-all;
  color: #ffdcdc;
  margin-bottom: 12px;
}

.fu-fail-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.fu-btn-copy {
  background: rgba(255, 80, 80, 0.2);
  border: 1px solid rgba(255, 80, 80, 0.3);
}
.fu-btn-copy:hover {
  background: rgba(255, 80, 80, 0.35);
}

.fu-btn-retry {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.fu-btn-retry:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* === 动画 === */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
